import React from 'react'

import {
  checkDateIsEqual,
  translateDayAbbreviation,
  translateMonthAbbreviation,
  translateMonthName,
} from '../../utils/helpers/date'
import { useCalendar } from './hooks/useCalendar'

import '../../static/css/global.css'
import './Calendar.css'

interface CalendarProps {
  locale?: string
  selectedDate: Date
  selectDate: (date: Date) => void
  firstWeekDayNumber?: number
  disableDayRestrictions?: boolean
}

export const Calendar: React.FC<CalendarProps> = ({
  locale = 'default',
  selectedDate: date,
  selectDate,
  firstWeekDayNumber = 2,
  disableDayRestrictions = false,
}) => {
  const { functions, state } = useCalendar({
    locale,
    selectedDate: date,
    firstWeekDayNumber,
  })

  React.useEffect(() => {
    functions.setSelectedMonthByIndex(date.getMonth())
    functions.setSelectedYear(date.getFullYear())
  }, [date])

  return (
    <div className='calendar'>
      <div className='calendar__header'>
        <div
          aria-hidden
          className='calendar__header__arrow__left'
          onClick={() => functions.onClickArrow('left')}
        />
        {state.mode === 'days' && (
          <div
            className='calendar__header__title'
            aria-hidden
            onClick={() => functions.setMode('monthes')}
          >
            {translateMonthName(state.monthesNames[state.selectedMonth.monthIndex].month)} &nbsp;
            {state.selectedYear}
          </div>
        )}
        {state.mode === 'monthes' && (
          <div
            className='calendar__header__title'
            aria-hidden
            onClick={() => functions.setMode('years')}
          >
            {state.selectedYear}
          </div>
        )}
        {state.mode === 'years' && (
          <div className='calendar__header__title'>
            {state.selectedYearsInterval[0]} -{' '}
            {state.selectedYearsInterval[state.selectedYearsInterval.length - 1]}
          </div>
        )}
        <div
          aria-hidden
          className='calendar__header__arrow__right'
          onClick={() => functions.onClickArrow('right')}
        />
      </div>
      <div className='calendar__body'>
        {state.mode === 'days' && (
          <>
            <div className='calendar__week__names'>
              {state.weekDaysNames.map((weekDaysName) => (
                <div key={weekDaysName.dayShort}>
                  {translateDayAbbreviation(weekDaysName.dayShort)}
                </div>
              ))}
            </div>
            <div className='calendar__days'>
              {state.calendarDays.map((day) => {
                const isSelectedDay = checkDateIsEqual(day.date, date) // Only check selectedDate
                const isAdditionalDay = day.monthIndex !== state.selectedMonth.monthIndex
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                const isPastDay = disableDayRestrictions ? false : day.date < today

                return (
                  <div
                    key={`${day.dayNumber}-${day.monthIndex}`}
                    aria-hidden={!isPastDay}
                    onClick={
                      isPastDay
                        ? undefined
                        : () => {
                            functions.setSelectedDay(day)
                            selectDate(day.date)
                          }
                    }
                    className={[
                      'calendar__day',
                      isSelectedDay ? 'calendar__selected__item' : '',
                      isAdditionalDay ? 'calendar__additional__day' : '',
                      isPastDay ? 'calendar__disabled__day' : '',
                    ].join(' ')}
                    aria-disabled={isPastDay}
                  >
                    {day.dayNumber}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {state.mode === 'monthes' && (
          <div className='calendar__pick__items__container'>
            {state.monthesNames.map((monthesName) => {
              const isSelectedMonth = monthesName.monthIndex === state.selectedMonth.monthIndex

              return (
                <div
                  key={monthesName.month}
                  aria-hidden
                  onClick={() => {
                    functions.setSelectedMonthByIndex(monthesName.monthIndex)
                    functions.setMode('days')
                  }}
                  className={[
                    'calendar__pick__item',
                    isSelectedMonth ? 'calendar__selected__item' : '',
                  ].join(' ')}
                >
                  {translateMonthAbbreviation(monthesName.monthShort)}
                </div>
              )
            })}
          </div>
        )}

        {state.mode === 'years' && (
          <div className='calendar__pick__items__container'>
            <div className='calendar__unchoosable__year'>{state.selectedYearsInterval[0] - 1}</div>
            {state.selectedYearsInterval.map((year) => {
              const isSelectedYear = year === state.selectedYear

              return (
                <div
                  key={year}
                  aria-hidden
                  onClick={() => {
                    functions.setSelectedYear(year)
                    functions.setMode('monthes')
                  }}
                  className={[
                    'calendar__pick__item',
                    isSelectedYear ? 'calendar__selected__item' : '',
                  ].join(' ')}
                >
                  {year}
                </div>
              )
            })}
            <div className='calendar__unchoosable__year'>
              {state.selectedYearsInterval[state.selectedYearsInterval.length - 1] + 1}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
